import axiosInstance from "../axiosInstance/axiosInstance";
import queryClient from "./queryClient";

import HouseKeeping from "../pages/PostCheckInRequest/HouseKeeping/HouseKeeping";
import FoodRequest from "../pages/PostCheckInRequest/FoodRequest/FoodRequest";
import PostCheckInTaxi from "../pages/PostCheckInRequest/Taxi/PostCheckInTaxi";
import SpaRequest from "../pages/PostCheckInRequest/Spa/SpaRequest";
import TableBooking from "../pages/PostCheckInRequest/TableBooking/TableBooking";
import WakeUpCall from "../pages/PostCheckInRequest/WakeUpCall/WakeUpCall";
import Wifi from "../pages/PostCheckInRequest/Wifi/Wifi";
import CustomRequest from "../pages/PostCheckInRequest/CustomRequest/CustomRequest";
import RoomChange from "../pages/PostCheckInRequest/RoomChange/RoomChange";
import LateCheckout from "../pages/PostCheckInRequest/LateCheckout/LateCheckout";
import ConferenceRoom from "../pages/PostCheckInRequest/ConferenceRoom/ConferenceRoom";
import CheckOut from "../pages/PostCheckInRequest/CheckOut/CheckOut";
import EarlyCheckIn from "../pages/EarlyCheckIn/EarlyCheckIn";
import CabBooking from "../pages/CabBooking/CabBooking";

// One entry per /bookings/:bookingId/requests/new/:service slug.
//
// `serviceName` is what the hotel's /services list calls it, `load` fetches
// whatever that screen needs on top of the shared booking context.

// Short enough that a room the hotel has just reassigned is not held onto,
// long enough that stepping between two request forms does not refetch.
const BOOKED_ROOMS_STALE_TIME = 5 * 1000;

// Menus, spa treatments and housekeeping services are hotel-level catalogues
// that barely move, so they are worth holding across screens.
const HOTEL_CATALOG_STALE_TIME = 5 * 60 * 1000;

const loadBookedRooms = async (ctx) => {
    const bookedRooms = await queryClient.fetchQuery({
        queryKey: ["booked-rooms", ctx.hotelId, ctx.bookingId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/api/v1/hotels/${ctx.hotelId}/bookings/${ctx.bookingId}/booked-rooms`);
            return response.data.data;
        },
        staleTime: BOOKED_ROOMS_STALE_TIME,
        // Fail as fast as these calls used to; the callers handle the error.
        retry: false,
    });

    return { bookedRooms };
};

const hotelCatalog = (request) => queryClient.fetchQuery({
    queryKey: ["hotel-catalog", request],
    queryFn: async () => {
        const response = await axiosInstance.get(request);
        return response.data.data;
    },
    staleTime: HOTEL_CATALOG_STALE_TIME,
    retry: false,
});

// The menu / services lists are what the guest picks from, but a failure there
// used to leave the form open with an empty list rather than blocking it.
const optional = async (request, fallback) => {
    try {
        return await hotelCatalog(request);
    } catch (error) {
        console.log(error);
        return fallback;
    }
};

const newRequestServices = {
    "housekeeping": {
        Component: HouseKeeping,
        serviceName: "HouseKeeping",
        load: async (ctx) => {
            const [rooms, housekeepingServices] = await Promise.all([
                loadBookedRooms(ctx),
                hotelCatalog(`/api/v1/hotels/${ctx.hotelId}/hotel-housekeeping-services`),
            ]);
            return { ...rooms, housekeepingServices };
        },
    },

    "food": {
        Component: FoodRequest,
        serviceName: "In Room Dining",
        load: async (ctx) => {
            const [rooms, menu] = await Promise.all([
                loadBookedRooms(ctx),
                optional(`/api/v1/hotels/${ctx.hotelId}/restaurant/menu-items`, []),
            ]);
            return { ...rooms, menu };
        },
    },

    "spa": {
        Component: SpaRequest,
        serviceName: "Spa",
        load: async (ctx) => {
            const [rooms, spaServices] = await Promise.all([
                loadBookedRooms(ctx),
                optional(`/api/v1/hotels/${ctx.hotelId}/spa-services`, []),
            ]);
            return { ...rooms, spaServices };
        },
    },

    "taxi": {
        Component: PostCheckInTaxi,
        serviceName: "PostCheckin Cab Request",
        load: loadBookedRooms,
    },

    "table-booking": {
        Component: TableBooking,
        serviceName: "Restaurant Table",
        load: loadBookedRooms,
    },

    "wakeup-call": {
        Component: WakeUpCall,
        serviceName: "Wake Up Call",
        load: loadBookedRooms,
    },

    "wifi": {
        Component: Wifi,
        serviceName: "Get Wifi",
        load: loadBookedRooms,
    },

    "custom": {
        Component: CustomRequest,
        serviceName: "Custom Request",
        load: loadBookedRooms,
    },

    "room-change": {
        Component: RoomChange,
        serviceName: "PostCheckin Room Change Request",
        load: loadBookedRooms,
    },

    "late-checkout": {
        Component: LateCheckout,
        serviceName: "PostCheckin Late Checkout Request",
        load: loadBookedRooms,
    },

    "conference-room": {
        Component: ConferenceRoom,
        serviceName: "Conference Room",
        load: loadBookedRooms,
    },

    "checkout": {
        Component: CheckOut,
        serviceName: "Checkout",
        load: loadBookedRooms,
    },

    "early-checkin": {
        Component: EarlyCheckIn,
        serviceName: "PreCheckin Early Checkin Request",
        load: loadBookedRooms,
    },

    "cab": {
        Component: CabBooking,
        serviceName: "PreCheckin Cab Request",
        // The cab form works off the booking alone
        load: async () => ({}),
    },
};

export default newRequestServices;
